import React from 'react';
import SEO from '../components/SEO';
import UsefulLinks from '../components/UsefulLinks';
import { MapPin, Trees, Waves, Palmtree, Shell, Sun, Fish } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const IleDeRe = () => {
  const attractions = [
    {
      icon: <Waves className="w-6 h-6" />,
      title: "Plages Exceptionnelles",
      description: "La Patache, Trousse Chemise, La Loge - des plages de sable fin réputées"
    },
    {
      icon: <Trees className="w-6 h-6" />,
      title: "Marais Salants",
      description: "Découvrez l'or blanc de l'île et ses traditions séculaires"
    },
    {
      icon: <Palmtree className="w-6 h-6" />,
      title: "Pistes Cyclables",
      description: "Plus de 100 km de pistes pour explorer l'île à vélo"
    },
    {
      icon: <Shell className="w-6 h-6" />,
      title: "Huîtres de Trousse Chemise",
      description: "Dégustez les meilleures huîtres directement chez le producteur"
    },
    {
      icon: <Sun className="w-6 h-6" />,
      title: "Climat Exceptionnel",
      description: "Plus de 2800 heures d'ensoleillement par an"
    },
    {
      icon: <Fish className="w-6 h-6" />,
      title: "Activités Nautiques",
      description: "Voile, paddle, kite-surf au Club Nautique des Portes"
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Île de Ré et Les Portes en Ré - Guide Tourisme 2026 | Villa Nomade"
        description="Découvrez l'île de Ré et Les Portes en Ré : plages, golf de Trousse Chemise, marais salants, activités. Guide complet pour vos vacances sur l'île de Ré en 2026."
        keywords="île de ré, les portes en ré, golf trousse chemise, plages ile de ré, marais salants, tourisme ile de ré, vacances charente maritime, plage patache"
      />
      
      {/* Hero */}
      <div className="relative h-[50vh] overflow-hidden">
        <img
          src="https://www.villanomadelesportesenre.com/medias/images/img-1715.jpg"
          alt="Île de Ré"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center text-white px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-serif mb-4">L'Île de Ré</h1>
            <p className="text-xl md:text-2xl">Perle de l'Atlantique</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <article className="prose prose-lg max-w-none">
          <h2 className="text-3xl font-serif mb-6 text-stone-800">L'Île de Ré : Un Paradis sur l'Atlantique</h2>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            L'<strong>île de Ré</strong>, située au large de La Rochelle en Charente-Maritime, est une destination prisée pour son climat exceptionnel, 
            ses plages de sable fin et son patrimoine authentique. Avec plus de 2800 heures d'ensoleillement par an, l'île offre 
            un cadre idyllique pour des vacances inoubliables.
          </p>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Les Portes en Ré : Le Joyau du Nord</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Situé à la pointe nord de l'île, <strong>Les Portes en Ré</strong> est un village préservé qui a su conserver son authenticité. 
            C'est le point de départ idéal pour explorer la réserve naturelle du Lilleau des Niges, où vous pourrez observer de nombreuses 
            espèces d'oiseaux migrateurs dans un cadre naturel préservé.
          </p>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Le Golf de Trousse Chemise</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Le <strong>golf de Trousse Chemise</strong> est l'un des plus beaux parcours de la région. Niché entre océan et marais salants, 
            ce golf 9 trous offre un cadre exceptionnel pour les amateurs de golf. La Villa Nomade se situe à seulement quelques 
            minutes à pied du green, permettant aux golfeurs de profiter pleinement de leur passion.
          </p>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Les Plages Magnifiques</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Les Portes en Ré abrite certaines des <strong>plus belles plages de l'île de Ré</strong> :
          </p>
          
          <ul className="space-y-3 text-stone-700 mb-6">
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>La plage de la Patache</strong> : Considérée comme l'une des plus belles plages de l'île, elle offre un coucher de soleil spectaculaire sur le Banc du Bûcheron</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>La plage de Trousse Chemise</strong> : Idéale pour les familles avec ses eaux calmes et son sable fin</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>La plage de la Loge</strong> : Site du Club Nautique, parfaite pour les sports nautiques</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>La plage des 3 Cailloux</strong> : Sauvage et préservée, un havre de paix</span>
            </li>
          </ul>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Les Marais Salants et l'Or Blanc</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Les <strong>marais salants des Portes en Ré</strong> sont un patrimoine vivant. La récolte du sel et de la fleur de sel 
            perpétue une tradition ancestrale. Ne manquez pas de visiter les producteurs locaux et de déguster les fameuses 
            <strong> huîtres de Trousse Chemise</strong>, élevées dans les claires environnantes.
          </p>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">À Vélo sur l'Île</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            L'<strong>île de Ré à vélo</strong> est une expérience incontournable. Plus de 100 km de pistes cyclables traversent l'île, 
            permettant de découvrir villages pittoresques, vignobles, plages et marais salants en toute sécurité. 
            Les Portes en Ré est parfaitement connecté à ce réseau cyclable.
          </p>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Activités et Loisirs</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Aux Portes en Ré, les activités ne manquent pas :
          </p>
          
          <ul className="space-y-3 text-stone-700 mb-6">
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>Sports nautiques</strong> : Voile, paddle, kite-surf au Club Nautique</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>Observation ornithologique</strong> : Réserve naturelle du Lilleau des Niges</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>Marchés locaux</strong> : Produits du terroir, poissons frais, artisanat</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>Phare des Baleines</strong> : Monument emblématique avec vue panoramique</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span><strong>Villages typiques</strong> : Saint-Martin-de-Ré, Ars-en-Ré, La Flotte</span>
            </li>
          </ul>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Gastronomie Rétaise</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            La gastronomie de l'<strong>île de Ré</strong> est réputée pour ses produits de la mer : huîtres, moules, poissons frais. 
            Ne manquez pas de goûter les pommes de terre de l'île (AOP), le sel et la fleur de sel, ainsi que le pineau des Charentes 
            et le cognac dans les nombreux restaurants et producteurs locaux.
          </p>

          <h3 className="text-2xl font-serif mb-4 text-amber-700">Séjourner aux Portes en Ré</h3>
          
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Pour profiter pleinement de votre séjour aux <strong>Portes en Ré</strong>, la <strong>Villa Nomade</strong> offre un hébergement 
            d'exception. Située entre le golf de Trousse Chemise et les plages, avec son spa de nage chauffé, elle constitue le camp de base 
            idéal pour explorer l'île de Ré en famille ou entre amis.
          </p>
        </article>

        {/* Attractions Grid */}
        <div className="mt-16">
          <h3 className="text-3xl font-serif mb-8 text-center text-stone-800">Découvrez l'Île de Ré</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {attractions.map((attraction, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center text-amber-700">
                    {attraction.icon}
                  </div>
                  <h4 className="text-xl font-semibold mb-2 text-stone-800">{attraction.title}</h4>
                  <p className="text-stone-600">{attraction.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-serif mb-4 text-stone-800">Situation Géographique</h3>
          <p className="text-stone-700 mb-4">
            L'île de Ré est reliée au continent par un pont de 3 km depuis 1988. Située à 30 minutes de La Rochelle, 
            elle est facilement accessible en voiture. Les Portes en Ré se trouve à l'extrémité nord de l'île, 
            à environ 30 km après le pont.
          </p>
          <p className="text-stone-700">
            <strong>Accès :</strong> Depuis La Rochelle, emprunter le pont de l'île de Ré (péage) puis suivre la D735 
            direction Les Portes en Ré (environ 45 minutes).
          </p>
        </div>
      </div>
      
      {/* Liens Utiles */}
      <UsefulLinks />
    </div>
  );
};

export default IleDeRe;
